/**
 * PORTED — RO-3-04 / #44297.
 * Origin: `excellent/app/market/_icp-client.tsx`.
 *
 * Changes:
 *   router.refresh() dropped (no server re-render); SWR mutate covers the
 *   market.page aggregate + marketIcp.list instead.
 * Live Next original UNTOUCHED (D6).
 */

"use client";

import { useState } from "react";
import {
  Button,
  Card,
  IntentBadge,
  Switch,
  Dialog,
  DialogContent,
  ModalHeader,
  FormShell,
  BriefFieldRow,
  SetupDriver,
  type SetupCard,
  type SetupPromptCard,
} from "@excellent/ui";
import type { BriefField } from "@excellent/core";
import { Plus } from "@excellent/ui/icons";
import { apiCall, apiKey } from "@excellent/ui/api";
import { useSWRConfig } from "swr";
import {
  marketIcp,
  type IcpProfileDTO,
} from "@excellent/ui/api/contracts/market-icp";
import { market } from "@excellent/ui/api/contracts/market";

// ─────────────────────────────────────────────────────────────────────────────
// Market Intelligence — the ICP segment create + edit affordance (task #462).
// The `/market` page renders ICP cards read-only; this client layer adds:
//  • "New segment" → a question-led SetupDriver walk (the shared stepper — never
//    forked), one prompt per field, that POSTs the segment on the last answer.
//  • Clicking a card → an edit surface (FormShell + BriefFieldRow) that PATCHes
//    each field on save, toggles is_primary, and archives the segment.
// Both go through the market-icp contract; no direct fetch, no NextResponse.
// ─────────────────────────────────────────────────────────────────────────────

/** The editable ICP fields, shared by the create walk and the edit surface so
 *  the two surfaces stay in lockstep. `name` is the only required field. */
const ICP_FIELDS: BriefField[] = [
  {
    key: "name",
    label: "Segment name",
    prompt: "What do you call this customer segment?",
    subhead: "A short handle you'll recognize — e.g. \"Mid-market SaaS ops\".",
    placeholder: "Mid-market SaaS ops",
    type: "text",
    priority: 5,
    required: true,
  },
  {
    key: "description",
    label: "Description",
    prompt: "In a sentence, who is this segment?",
    subhead: "The one-line summary Sales and Marketing read from.",
    type: "longtext",
    priority: 4,
    required: false,
  },
  {
    key: "firmographics",
    label: "Firmographics",
    prompt: "What do these companies (or people) look like?",
    subhead: "Size, industry, geography, stage — the shape of a good fit.",
    type: "longtext",
    priority: 3,
    required: false,
  },
  {
    key: "pains",
    label: "Pains",
    prompt: "What problems are they trying to solve?",
    subhead: "The pain your product speaks to — why they'd buy.",
    type: "longtext",
    priority: 3,
    required: false,
  },
  {
    key: "buying_triggers",
    label: "Buying triggers",
    prompt: "What event tells you they're ready to buy?",
    subhead: "New funding, a hire, a tool switch — the moment to reach out.",
    type: "longtext",
    priority: 2,
    required: false,
  },
  {
    key: "disqualifiers",
    label: "Disqualifiers",
    prompt: "What rules a prospect out of this segment?",
    subhead: "The anti-signals that save you from a bad-fit deal.",
    type: "longtext",
    priority: 2,
    required: false,
  },
  {
    key: "channels",
    label: "Channels",
    prompt: "Where do you reach this segment?",
    subhead: "The channels where you'll find and engage them.",
    type: "longtext",
    priority: 2,
    required: false,
  },
];

type IcpDraft = Record<string, string>;

export function IcpSection({ profiles }: { profiles: IcpProfileDTO[] }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<IcpProfileDTO | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="type-title font-medium text-foreground">ICP segments</h2>
        <Button
          variant="outline"
          size="icon-tb"
          aria-label="New segment"
          title="New segment"
          onClick={() => setCreating(true)}
        >
          <Plus strokeWidth={1.5} className="h-4 w-4" />
        </Button>
      </div>

      {profiles.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {profiles.map((s) => (
            <Card
              key={s.id}
              interactive
              className="p-4 text-left"
              onClick={() => setEditing(s)}
            >
              <div className="flex items-center gap-2">
                <span className="type-body font-medium text-foreground">{s.name}</span>
                {s.is_primary === 1 ? (
                  <IntentBadge intent="info">primary</IntentBadge>
                ) : null}
              </div>
              {s.description ? (
                <p className="mt-1 type-body text-muted-foreground">{s.description}</p>
              ) : null}
              {s.firmographics ? (
                <p className="mt-2 type-subtext text-muted-foreground">
                  <span className="text-foreground">Firmographics:</span>{" "}
                  {s.firmographics}
                </p>
              ) : null}
              {s.pains ? (
                <p className="mt-0.5 type-subtext text-muted-foreground">
                  <span className="text-foreground">Pains:</span> {s.pains}
                </p>
              ) : null}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-6">
          <p className="max-w-prose type-body text-muted-foreground">
            No ICP segments yet. Define who you serve so Sales and Marketing have
            something to target.
          </p>
        </Card>
      )}

      <CreateSegmentDialog
        open={creating}
        onOpenChange={setCreating}
      />
      <EditSegmentDialog
        profile={editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </div>
  );
}

/** Revalidate the ICP list + the market page aggregate (RO-3-04). */
function useRefreshIcp() {
  const { mutate } = useSWRConfig();
  return async () => {
    await mutate(apiKey(marketIcp.list, {}));
    await mutate(apiKey(market.page, {}));
  };
}

// ---- Create: the question-led SetupDriver walk ----

function CreateSegmentDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const refresh = useRefreshIcp();
  const [draft, setDraft] = useState<IcpDraft>({});
  const [error, setError] = useState<string | null>(null);

  // One prompt card per field. The answer commits into the local draft; the
  // final field's save POSTs the whole segment (the SetupDriver save is
  // optimistic, so a rejected promise rolls back to the card with the draft).
  const fields = ICP_FIELDS;
  const cards: SetupCard[] = fields.map((f, i) => ({
    mode: "prompt",
    key: f.key,
    title: f.label,
    question: f.prompt,
    sublabel: f.required
      ? `${f.subhead ?? ""} · required`.trim()
      : f.subhead,
    placeholder: f.placeholder,
    // The name field must be non-empty; optional fields can be saved blank.
    canSave: f.required
      ? (v) => typeof v === "string" && v.trim().length > 0
      : () => true,
  } satisfies SetupPromptCard));

  async function onAnswer(card: SetupPromptCard, value: unknown) {
    const text = typeof value === "string" ? value.trim() : "";
    const next = { ...draft, [card.key]: text };
    setDraft(next);
    // The last field commits the segment.
    if (card.key === fields[fields.length - 1]!.key) {
      setError(null);
      try {
        await apiCall(marketIcp.create, {
          body: {
            name: next.name ?? "",
            description: next.description || null,
            firmographics: next.firmographics || null,
            pains: next.pains || null,
            buying_triggers: next.buying_triggers || null,
            disqualifiers: next.disqualifiers || null,
            channels: next.channels || null,
          },
        });
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        throw err; // SetupDriver rolls back to this card so the user can retry.
      }
    }
  }

  function reset() {
    setDraft({});
    setError(null);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <ModalHeader
          title="Define a segment"
          description="Answer a few quick questions to map an ideal-customer profile."
        />
        <div className="relative px-1 pb-6 pt-2">
          <SetupDriver
            cards={cards}
            onAnswer={onAnswer}
            showDismiss={false}
            onExhausted={() => {
              reset();
              onOpenChange(false);
            }}
            copy={{
              finishTitle: () => "Segment saved",
              finishBody: () => "Your new ICP segment is on the /market hub.",
            }}
          />
          {error ? (
            <p className="mt-2 type-subtext text-destructive">{error}</p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Edit: the FormShell + BriefFieldRow surface ----

function EditSegmentDialog({
  profile,
  onOpenChange,
}: {
  profile: IcpProfileDTO | null;
  onOpenChange: (open: boolean) => void;
}) {
  const refresh = useRefreshIcp();
  const [busy, setBusy] = useState(false);

  async function saveField(key: string, value: unknown) {
    if (!profile) return;
    const text = typeof value === "string" ? value : "";
    await apiCall(marketIcp.update, {
      body: { id: profile.id, [key]: key === "name" ? text : text || null },
    });
    await refresh();
  }

  async function togglePrimary(next: boolean) {
    if (!profile || busy) return;
    setBusy(true);
    try {
      await apiCall(marketIcp.update, {
        body: { id: profile.id, is_primary: next },
      });
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (!profile || busy) return;
    setBusy(true);
    try {
      await apiCall(marketIcp.archive, { body: { id: profile.id } });
      await refresh();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={profile != null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <ModalHeader
          title="Edit segment"
          description="Changes save as you go."
        />
        {profile ? (
          <FormShell
            compact
            footer={
              <Button
                variant="destructive"
                disabled={busy}
                onClick={archive}
                className="justify-center"
              >
                Archive segment
              </Button>
            }
          >
            {ICP_FIELDS.map((f) => (
              <BriefFieldRow
                key={f.key}
                field={f}
                initial={
                  (profile[f.key as keyof IcpProfileDTO] as string | null) ?? ""
                }
                onSave={saveField}
              />
            ))}
            <div className="flex items-center justify-between border-t border-border/40 pt-4">
              <div className="flex flex-col">
                <span className="type-body font-medium text-foreground">
                  Primary segment
                </span>
                <span className="type-subtext text-muted-foreground">
                  The headline ICP shown across Sales and Marketing.
                </span>
              </div>
              <Switch
                checked={profile.is_primary === 1}
                disabled={busy}
                onCheckedChange={togglePrimary}
                aria-label="Primary segment"
              />
            </div>
          </FormShell>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
